import { useEffect, useMemo, useState } from 'react';
import { NominationRole } from '../types/nomination';
import { User } from '../types/user';
import { mapUserRoleToNominationRole } from '../utils/nominationPermissions';
import {
  getCachedSharedNominationSettings,
  loadSharedNominationSettings,
  NominationSettings,
  onNominationSettingsChange
} from '../services/nominationSettings';

interface UseNominationRoleResult {
  nominationRole: NominationRole;
  isLoading: boolean;
}

let nominationRoleHookRuns = 0;
let nominationRoleSettingsLoads = 0;
let lastNominationRoleUserKey = '';
const nominationRoleCache = new Map<string, NominationRole>();

function getRoleCacheKey(userRole?: string, userEmail?: string, settings?: NominationSettings | null): string {
  return [
    userRole || '',
    (userEmail || '').trim().toLowerCase(),
    (settings?.receiverEmail || '').trim().toLowerCase(),
    (settings?.ccEmail || '').trim().toLowerCase(),
  ].join('|');
}

export function useNominationRole(user: User | null | undefined): UseNominationRoleResult {
  const hookRunId = ++nominationRoleHookRuns;
  const hookRenderStart = performance.now();
  console.log('[Perf][NominationRole] hook render start', {
    hookRunId,
    userEmail: user?.email,
    userRole: user?.role,
  });
  const [settings, setSettings] = useState<NominationSettings | null>(() => getCachedSharedNominationSettings());
  const [isLoading, setIsLoading] = useState(false);
  const userEmail = user?.email;
  const userRole = user?.role;

  useEffect(() => {
    if (!userEmail || !userRole) {
      setIsLoading(false);
      console.log('[Perf][NominationRole][LoadingFalse]', {
        reason: 'missing user email or role',
        userEmail,
        userRole,
      });
      return;
    }

    let mounted = true;
    const refreshNominationSettings = (options: { forceRefresh?: boolean; background?: boolean } = {}) => {
      const cachedSettings = getCachedSharedNominationSettings();
      if (!options.forceRefresh && cachedSettings) {
        setSettings(cachedSettings);
        setIsLoading(false);
        console.log('[Perf][NominationRole][FallbackSettings]', {
          reason: 'using shared memory cache',
          userEmail,
          userRole,
          receiverEmail: cachedSettings.receiverEmail,
          ccEmail: cachedSettings.ccEmail,
        });
        return;
      }

      const loadId = ++nominationRoleSettingsLoads;
      const startedAt = performance.now();
      console.log('[Perf][NominationRole][Start]', {
        loadId,
        hookRunId,
        userEmail,
        userRole,
      });
      console.log('[Perf][NominationRole] settings load start', {
        loadId,
        hookRunId,
        userEmail,
        userRole,
      });
      if (!options.background && !cachedSettings) {
        setIsLoading(true);
      }
      loadSharedNominationSettings({ forceRefresh: options.forceRefresh })
        .then(loadedSettings => {
          if (!mounted) return;
          console.log('[Perf][NominationRole] settings load complete', {
            loadId,
            hookRunId,
            durationMs: Math.round(performance.now() - startedAt),
            receiverEmail: loadedSettings.receiverEmail,
            ccEmail: loadedSettings.ccEmail,
          });
          console.log('[Perf][NominationRole][SettingsLoaded]', {
            loadId,
            hookRunId,
            durationMs: Math.round(performance.now() - startedAt),
            receiverEmail: loadedSettings.receiverEmail,
            ccEmail: loadedSettings.ccEmail,
          });
          setSettings(loadedSettings);
        })
        .catch(error => {
          console.warn('[Perf][NominationRole] settings load failed', {
            loadId,
            hookRunId,
            durationMs: Math.round(performance.now() - startedAt),
            error,
          });
        })
        .finally(() => {
          if (!mounted) return;
          setIsLoading(false);
          console.log('[Perf][NominationRole][LoadingFalse]', {
            loadId,
            hookRunId,
            durationMs: Math.round(performance.now() - startedAt),
            userEmail,
            userRole,
          });
        });
    };

    const userKey = `${userEmail.toLowerCase()}|${userRole}`;
    const userChanged = Boolean(lastNominationRoleUserKey && lastNominationRoleUserKey !== userKey);
    lastNominationRoleUserKey = userKey;
    refreshNominationSettings({
      forceRefresh: userChanged,
      background: userChanged && Boolean(getCachedSharedNominationSettings()),
    });
    const unsubscribe = onNominationSettingsChange(updatedSettings => {
      setSettings(updatedSettings);
    });
    const refreshOnFocus = () => refreshNominationSettings({ forceRefresh: true, background: true });
    window.addEventListener('focus', refreshOnFocus);

    return () => {
      mounted = false;
      unsubscribe();
      window.removeEventListener('focus', refreshOnFocus);
    };
    // Note: hookRunId is intentionally omitted below. It is a monotonically increasing
    // per-render counter used only to correlate log lines; it changes on every render, so
    // including it as a dependency would re-run this settings-refresh effect on every render
    // instead of only when the user identity changes (defeating its purpose and re-fetching
    // settings unnecessarily).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userEmail, userRole]);

  const nominationRole = useMemo(() => {
    const startedAt = performance.now();
    const roleCacheKey = getRoleCacheKey(userRole, userEmail, settings);
    const cachedRole = nominationRoleCache.get(roleCacheKey);
    const calculatedRole = cachedRole || (userRole ? mapUserRoleToNominationRole(userRole, userEmail, settings || undefined) : NominationRole.EMPLOYEE);
    if (!cachedRole) {
      nominationRoleCache.set(roleCacheKey, calculatedRole);
    }
    console.log('[Perf][NominationRole] role calculation complete', {
      hookRunId,
      durationMs: Math.round(performance.now() - startedAt),
      userEmail,
      userRole,
      nominationRole: calculatedRole,
      hasLoadedSettings: Boolean(settings),
    });
    console.log('[Perf][NominationRole][RoleComputed]', {
      hookRunId,
      durationMs: Math.round(performance.now() - startedAt),
      userEmail,
      userRole,
      nominationRole: calculatedRole,
      hasLoadedSettings: Boolean(settings),
    });
    return calculatedRole;
    // Note: hookRunId is intentionally omitted below (see rationale above) — it changes on
    // every render and is used only for log correlation, not for the role computation itself.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings, userEmail, userRole]);

  useEffect(() => {
    console.log('[Perf][NominationRole] hook render complete', {
      hookRunId,
      durationMs: Math.round(performance.now() - hookRenderStart),
      userEmail,
      userRole,
      nominationRole,
      isLoading,
    });
  });

  return { nominationRole, isLoading };
}
